import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, CheckCircle2, ArrowRight, AlertCircle, Loader2 } from "lucide-react";

const EXPERIENCE_OPTIONS = ["Novice", "Intermediate", "Advanced", "Veteran"];

const ROLE_OPTIONS = [
  { value: "Chaser", desc: "Field intercept driver / navigator" },
  { value: "Radar Team", desc: "Mobile radar & mesonet operator" },
  { value: "Forecaster", desc: "Pre-chase target & nowcast analyst" },
  { value: "Logger", desc: "Data logging & chase documentation" },
  { value: "Communications", desc: "Comms relay, NWS reporting" },
];

const initial = {
  full_name: "",
  email: "",
  phone: "",
  role: "",
  experience_level: "",
  why_join: "",
  chase_vehicle: "",
  equipment: "",
  region: "",
  availability: "",
  emergency_contact_name: "",
  emergency_contact_phone: "",
};

export default function Apply() {
  const [form, setForm] = useState(initial);
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeBase64, setResumeBase64] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: null }));
  };

  const handleResume = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Resume must be under 5 MB");
      return;
    }
    setResumeFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const base64 = typeof result === "string" ? result.split(",")[1] : null;
      setResumeBase64(base64);
    };
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const errs = {};
    const required = [
      "full_name", "email", "phone", "role", "experience_level", "why_join",
      "chase_vehicle", "equipment", "region", "availability",
      "emergency_contact_name", "emergency_contact_phone",
    ];
    required.forEach((k) => {
      if (!form[k] || String(form[k]).trim().length === 0) errs[k] = "Required";
    });
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Invalid email";
    if (form.why_join && form.why_join.length < 10) errs.why_join = "Tell us more (min 10 chars)";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        resume_filename: resumeFile?.name || null,
        resume_data_base64: resumeBase64 || null,
      };
      await api.post("/applications", payload);
      setSuccess(true);
      toast.success("Application submitted! We'll be in touch.");
      setForm(initial);
      setResumeFile(null);
      setResumeBase64(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="grain section-pad" data-testid="apply-success">
        <div className="container-vg max-w-2xl text-center">
          <div className="w-20 h-20 mx-auto bg-green-500/10 border border-green-500/40 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
          </div>
          <div className="overline mt-8 !text-green-400">Transmission Received</div>
          <h1 className="font-heading text-4xl md:text-6xl font-black uppercase tracking-tighter mt-4">
            We've got your <span className="text-yellow-400">application.</span>
          </h1>
          <p className="text-zinc-400 mt-6 text-lg leading-relaxed">
            Our crew director will review your submission within 5–7 business days. If your profile matches our 2026
            roster needs, you'll get an email to schedule a video interview.
          </p>
          <button onClick={() => setSuccess(false)} className="btn-secondary mt-10" data-testid="apply-submit-another">
            Submit another <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  const FieldErr = ({ name }) =>
    errors[name] ? (
      <div className="text-red-400 text-xs mt-1 font-mono uppercase tracking-wider flex items-center gap-1">
        <AlertCircle className="w-3 h-3" /> {errors[name]}
      </div>
    ) : null;

  return (
    <div className="grain section-pad" data-testid="apply-page">
      <div className="container-vg max-w-4xl">
        <div className="overline">Application · 2026 Season</div>
        <h1 className="font-heading text-5xl md:text-7xl font-black uppercase tracking-tighter mt-4">
          Join the <span className="text-yellow-400">chase.</span>
        </h1>
        <p className="text-zinc-400 mt-6 text-lg max-w-2xl">
          Complete the form below. All fields marked are required. Resume upload is optional but encouraged.
        </p>

        <form onSubmit={onSubmit} className="mt-12 space-y-12" data-testid="apply-form">
          {/* SECTION 0 — Role */}
          <div>
            <div className="flex items-center gap-4 mb-6">
              <span className="font-mono text-yellow-400 text-sm">01.</span>
              <h2 className="font-heading text-2xl font-semibold">Which role are you applying for?</h2>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {ROLE_OPTIONS.map((r) => {
                const active = form.role === r.value;
                return (
                  <button
                    type="button"
                    key={r.value}
                    onClick={() => update("role", r.value)}
                    className={`text-left p-5 border transition-colors ${
                      active
                        ? "border-yellow-400 bg-yellow-400/5"
                        : "border-white/10 hover:border-white/30"
                    }`}
                    data-testid={`apply-role-${r.value.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-heading text-lg font-semibold text-white">{r.value}</div>
                      <span
                        className={`w-3 h-3 border ${
                          active ? "bg-yellow-400 border-yellow-400" : "border-white/30"
                        }`}
                      />
                    </div>
                    <div className="text-xs text-zinc-400 font-mono uppercase tracking-wider mt-2">{r.desc}</div>
                  </button>
                );
              })}
            </div>
            <FieldErr name="role" />
          </div>

          {/* SECTION 1 */}
          <div>
            <div className="flex items-center gap-4 mb-6">
              <span className="font-mono text-yellow-400 text-sm">02.</span>
              <h2 className="font-heading text-2xl font-semibold">Personal Information</h2>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Full Name" name="full_name" value={form.full_name} onChange={update} error={errors.full_name} />
              <Field label="Email" name="email" type="email" value={form.email} onChange={update} error={errors.email} />
              <Field label="Phone" name="phone" value={form.phone} onChange={update} error={errors.phone} placeholder="+1 (555) 555-0100" />
              <div>
                <label className="overline mb-2 block !text-zinc-400">Experience Level *</label>
                <select
                  value={form.experience_level}
                  onChange={(e) => update("experience_level", e.target.value)}
                  className="input-vg"
                  data-testid="apply-field-experience_level"
                >
                  <option value="">Select level…</option>
                  {EXPERIENCE_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
                <FieldErr name="experience_level" />
              </div>
            </div>
            <div className="mt-6">
              <label className="overline mb-2 block !text-zinc-400">Why do you want to join Vortex Ghost? *</label>
              <textarea
                rows={5}
                value={form.why_join}
                onChange={(e) => update("why_join", e.target.value)}
                className="input-vg resize-none"
                placeholder="Tell us about your motivation, what you bring, and what you hope to learn…"
                data-testid="apply-field-why_join"
              />
              <FieldErr name="why_join" />
            </div>
          </div>

          {/* SECTION 2 */}
          <div>
            <div className="flex items-center gap-4 mb-6">
              <span className="font-mono text-yellow-400 text-sm">03.</span>
              <h2 className="font-heading text-2xl font-semibold">Field Capability</h2>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Chase Vehicle" name="chase_vehicle" value={form.chase_vehicle} onChange={update} error={errors.chase_vehicle} placeholder="e.g., 2018 Ford F-150 4WD" />
              <Field label="Operating Region" name="region" value={form.region} onChange={update} error={errors.region} placeholder="e.g., Central OK, North TX" />
            </div>
            <div className="mt-6">
              <label className="overline mb-2 block !text-zinc-400">Equipment You Own *</label>
              <textarea
                rows={3}
                value={form.equipment}
                onChange={(e) => update("equipment", e.target.value)}
                className="input-vg resize-none"
                placeholder="Mobile internet, GPS, anemometer, cameras, ham radio, etc."
                data-testid="apply-field-equipment"
              />
              <FieldErr name="equipment" />
            </div>
            <div className="mt-6">
              <label className="overline mb-2 block !text-zinc-400">Availability *</label>
              <input
                value={form.availability}
                onChange={(e) => update("availability", e.target.value)}
                className="input-vg"
                placeholder="e.g., Weekends Mar–Jul, full weeks in May"
                data-testid="apply-field-availability"
              />
              <FieldErr name="availability" />
            </div>
          </div>

          {/* SECTION 3 */}
          <div>
            <div className="flex items-center gap-4 mb-6">
              <span className="font-mono text-yellow-400 text-sm">04.</span>
              <h2 className="font-heading text-2xl font-semibold">Emergency Contact</h2>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Emergency Contact Name" name="emergency_contact_name" value={form.emergency_contact_name} onChange={update} error={errors.emergency_contact_name} />
              <Field label="Emergency Contact Phone" name="emergency_contact_phone" value={form.emergency_contact_phone} onChange={update} error={errors.emergency_contact_phone} />
            </div>
          </div>

          {/* SECTION 4 - Resume */}
          <div>
            <div className="flex items-center gap-4 mb-6">
              <span className="font-mono text-yellow-400 text-sm">05.</span>
              <h2 className="font-heading text-2xl font-semibold">Resume <span className="text-zinc-500 text-base font-normal">(optional)</span></h2>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <label className="block border-2 border-dashed border-white/15 hover:border-yellow-400 transition-colors p-10 text-center cursor-pointer" data-testid="apply-resume-dropzone">
              <input type="file" className="hidden" onChange={handleResume} accept=".pdf,.doc,.docx,.txt,image/*" data-testid="apply-resume-input" />
              <Upload className="w-8 h-8 text-yellow-400 mx-auto" />
              <div className="font-heading text-lg mt-4 text-white">
                {resumeFile ? resumeFile.name : "Drop a resume or click to upload"}
              </div>
              <div className="text-xs text-zinc-500 font-mono uppercase tracking-wider mt-2">
                PDF, DOC, TXT or image · Max 5MB
              </div>
            </label>
          </div>

          <div className="pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
            <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider max-w-md">
              By submitting you acknowledge that storm chasing is inherently dangerous and that you understand
              Vortex Ghost's safety protocols are non-negotiable.
            </p>
            <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed" data-testid="apply-submit-btn">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Submit Application <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, name, type = "text", value, onChange, error, placeholder }) {
  return (
    <div>
      <label className="overline mb-2 block !text-zinc-400">{label} *</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        className="input-vg"
        placeholder={placeholder}
        data-testid={`apply-field-${name}`}
      />
      {error && (
        <div className="text-red-400 text-xs mt-1 font-mono uppercase tracking-wider flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> {error}
        </div>
      )}
    </div>
  );
}
